import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { LineMask, FadeIn } from "@/components/Reveal";
import { services } from "@/data/services";

const steps = [
  { number: "01", title: "Listen & scope", text: "Every commission begins with your context, your challenges and your definition of success." },
  { number: "02", title: "Assess & evidence", text: "We gather evidence through audit, observation, data analysis and conversation." },
  { number: "03", title: "Recommend", text: "Clear, achievable recommendations — prioritised for impact, written to be acted on." },
  { number: "04", title: "Support & embed", text: "We stay alongside you to implement change and make improvement stick." },
];

export default function Services() {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
      }
    }
  }, [hash]);

  return (
    <div data-testid="services-page">
      <section className="bg-paper pt-44 pb-20 lg:pb-28" data-testid="services-hero">
        <div className="mx-auto max-w-[1600px] px-6 lg:px-12 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-3">
            <FadeIn>
              <p className="font-mono text-[11px] tracking-[0.24em] uppercase text-brand">
                Services
              </p>
            </FadeIn>
          </div>
          <div className="md:col-span-8 md:col-start-5">
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.98] tracking-tight text-ink">
              <LineMask delay={0.2} lines={["Specialist expertise,", "on your side."]} />
            </h1>
            <FadeIn delay={0.5} className="mt-8 max-w-2xl">
              <p className="text-base sm:text-lg leading-relaxed text-ink/65">
                Independent quality assurance, audit, policy writing, inspection
                readiness, post-inspection support, practice improvement and training
                for Youth Justice Services and their partners. Every commission is
                tailored — shaped around your service, your risks and your ambitions.
                Our audit and assurance work is aligned to the HMI Probation Case
                Assessment Rules and Guidance (CARaG) — the same rules and guidance
                inspectors use to assess casework with children and victims.
              </p>
            </FadeIn>
          </div>
        </div>
      </section>

      <section className="bg-paper pb-28 lg:pb-40" data-testid="services-list">
        <div className="mx-auto max-w-[1600px] px-6 lg:px-12 border-t border-ink/10">
          {services.map((service, i) => {
            const Icon = service.icon;
            return (
              <FadeIn key={service.id}>
                <article
                  id={service.id}
                  data-testid={`service-${service.id}`}
                  className="grid grid-cols-1 md:grid-cols-12 gap-8 border-b border-ink/10 py-16 lg:py-20 scroll-mt-28"
                >
                  <div className="md:col-span-2 flex md:flex-col items-center md:items-start gap-4">
                    <span className="font-mono text-sm text-slateblue">{service.number}</span>
                    <Icon className={`h-7 w-7 ${i % 2 ? "text-teal" : "text-brand"}`} strokeWidth={1.5} />
                  </div>
                  <div className="md:col-span-5">
                    <h2 className="font-display text-3xl sm:text-4xl text-ink">
                      {service.title}
                    </h2>
                    <p className="mt-5 text-base leading-relaxed text-ink/65">
                      {service.summary}
                    </p>
                  </div>
                  <div className="md:col-span-4 md:col-start-9">
                    <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-slateblue mb-5">
                      Typical deliverables
                    </p>
                    <ul className="space-y-3">
                      {service.deliverables.map((item) => (
                        <li key={item} className="flex items-start gap-3 text-sm text-ink/75">
                          <span className="mt-[7px] h-1.5 w-1.5 shrink-0 bg-brand" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </article>
              </FadeIn>
            );
          })}
        </div>
      </section>

      <section className="bg-cream py-28 lg:py-40" data-testid="services-framework">
        <div className="mx-auto max-w-[1600px] px-6 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-3">
              <FadeIn>
                <p className="font-mono text-[11px] tracking-[0.24em] uppercase text-brand">
                  Framework-aligned
                </p>
              </FadeIn>
            </div>
            <div className="md:col-span-8 md:col-start-5">
              <h2 className="font-display font-semibold text-3xl sm:text-4xl lg:text-5xl leading-tight text-ink">
                <LineMask inView lines={["The areas inspectors assess,", "built into every commission."]} />
              </h2>
              <FadeIn delay={0.3} className="mt-8 max-w-2xl">
                <p className="text-base sm:text-lg leading-relaxed text-ink/65">
                  Our audit, assurance and readiness work follows the structure of the
                  Case Assessment Rules and Guidance — with Child First principles, the
                  voice of the child, and work with victims under the Victims' Standard
                  running through each area.
                </p>
              </FadeIn>
            </div>
          </div>
          <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-10">
            {[
              { n: "01", slug: "assessing", t: "Assessing", d: "Analysis that focuses on positive change, safety and wellbeing, diversity and the whole child." },
              { n: "02", slug: "planning", t: "Planning", d: "Plans co-produced with the child and their family, building safety and stability as foundations for change." },
              { n: "03", slug: "delivery", t: "Delivery", d: "Well-focused, personalised and coordinated services that achieve positive change and keep people safe." },
              { n: "04", slug: "management-oversight", t: "Management Oversight", d: "The grip, direction and challenge that turns individual casework into consistently good practice." },
            ].map((area, i) => (
              <FadeIn key={area.n} delay={i * 0.08}>
                <div className="border-t-2 border-ink pt-8" data-testid={`framework-${area.slug}`}>
                  <p className="font-mono text-sm text-slateblue">{area.n}</p>
                  <h3 className="mt-4 font-display text-2xl text-ink">{area.t}</h3>
                  <p className="mt-4 text-sm leading-relaxed text-ink/60">{area.d}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section className="noise-overlay bg-ink py-28 lg:py-40 text-paper" data-testid="services-process">
        <div className="relative z-10 mx-auto max-w-[1600px] px-6 lg:px-12">
          <FadeIn>
            <p className="font-mono text-[11px] tracking-[0.24em] uppercase text-brand">
              How a commission works
            </p>
          </FadeIn>
          <h2 className="mt-6 font-display text-4xl sm:text-5xl leading-tight max-w-3xl">
            <LineMask inView lines={["A clear process,", "from first call to lasting change."]} />
          </h2>
          <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
            {steps.map((step, i) => (
              <FadeIn key={step.number} delay={i * 0.08} className="bg-ink">
                <div className="p-10 h-full" data-testid={`step-${step.number}`}>
                  <p className="font-mono text-sm text-brand">{step.number}</p>
                  <h3 className="mt-6 font-display text-2xl">{step.title}</h3>
                  <p className="mt-4 text-sm leading-relaxed text-paper/60">{step.text}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-cream py-28 lg:py-36" data-testid="services-cta">
        <div className="mx-auto max-w-[1600px] px-6 lg:px-12 grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <h2 className="md:col-span-8 font-display text-4xl sm:text-5xl leading-tight text-ink">
            <LineMask inView lines={["Not sure where to start?", "Let's talk it through."]} />
          </h2>
          <FadeIn delay={0.2} className="md:col-span-4 md:justify-self-end">
            <Link
              to="/contact"
              data-testid="services-cta-button"
              className="inline-flex items-center gap-2 rounded-full bg-ink px-8 py-4 text-sm tracking-wide text-paper transition-colors duration-300 hover:bg-brand"
            >
              Contact Us
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
